import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, Label, RevealHeading } from "@/components/site/Motion";
import { CTASection } from "@/components/site/Sections";
import { DIVISIONS } from "@/data/content";

export default function Division() {
  const { key } = useParams();
  const d = DIVISIONS[key];
  if (!d) return <Navigate to="/" replace />;
  const Icon = d.icon;

  return (
    <div data-testid={`division-${d.key}`}>
      <section className="relative min-h-[85vh] flex items-end overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 z-0">
          <img src={d.hero} alt={d.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/60 to-navy-deep/30" />
        </div>
        <div className="relative z-10 w-full mx-auto max-w-7xl px-5 lg:px-8 pb-16 pt-40">
          <div className="flex items-center gap-3 mb-6">
            <Icon className="h-7 w-7 text-gold" strokeWidth={1.3} />
            <Label>Division</Label>
          </div>
          <h1 className="font-display text-white text-5xl md:text-7xl font-medium tracking-tighter leading-none">
            <RevealHeading lines={[d.name]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 max-w-xl text-lg text-white/70"
          >
            {d.tagline}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <Label>Overview</Label>
            <Reveal>
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-light tracking-tight text-navy leading-tight">
                {d.intro}
              </h2>
            </Reveal>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-navy font-medium hover:text-gold transition-colors">
              Enquire about {d.name} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <Label>What we offer</Label>
            <div className="mt-6 grid gap-px bg-navy/10 border border-navy/10 sm:grid-cols-2">
              {d.offerings.map((o, i) => (
                <Reveal key={o} delay={(i % 2) * 0.05}>
                  <div className="flex items-start gap-3 bg-cream p-6 h-full">
                    <Check className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <span className="text-navy/70">{o}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {d.gallery.map((g, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="group relative h-72 overflow-hidden rounded-sm">
                <img src={g} alt={`${d.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
